import { memo, useEffect, useState } from "react";
import { useHandleDropdownChange } from "services/forms/hooks";
import { FleetTireSetEditFormValues } from "services/api/fleet/tires/types";
import { getFleetTireSetByVehicleActiveAPI } from "services/api/fleet/tires/api";
import { getDimensionSet, getTireManufacturers, getTireType } from "services/api/fleet/tires/helpers";
import { initialFleetTireSetEditForm } from "services/api/fleet/tires/consts";
import WarehouseDropdown from "ui/components/Dropdown/WarehouseDropdown/WarehouseDropdown";
import Badge from "ui/components/Badge/Badge";
import { Loader } from "ui/components/Loader";
import { Errors } from "services/forms/validation/types";
import { MountFormValues } from "../types";

interface MountStep2Props {
  formValues: MountFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<MountFormValues>>;
  errors: Errors;
}

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-start py-2.5">
    <span className="text-sm text-slate-500 min-w-[140px]">{label}</span>
    <div className="text-sm font-medium text-slate-900 text-right">{children}</div>
  </div>
);

const MountStep2: React.FC<MountStep2Props> = ({ formValues, setFormValues, errors }) => {
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSetOnVehicle, setActiveSetOnVehicle] = useState<FleetTireSetEditFormValues>(initialFleetTireSetEditForm);

  useEffect(() => {
    if (formValues.targetVehicleId) fetchActiveSetOnVehicle(formValues.targetVehicleId);
  }, [formValues.targetVehicleId]);

  const fetchActiveSetOnVehicle = async (vehicleId: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetTireSetByVehicleActiveAPI(vehicleId);
      if (result) setActiveSetOnVehicle(result);
    } catch { /* */ }
    setIsLoading(false);
  };

  const activeTireType = getTireType(activeSetOnVehicle.tires);
  const activeDimension = getDimensionSet(activeSetOnVehicle.tires);
  const activeManufacturers = getTireManufacturers(activeSetOnVehicle.tires);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
        <div className="w-full max-w-2xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Trenutni set na vozilu - za demontažu</h1>
          <p className="text-center text-md text-slate-500 mb-8">Odaberite u koje skladište ide trenutni set nakon demontaže.</p>
          <div className="relative h-[200px]"><Loader isLoading={true}>{null}</Loader></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
      <div className="w-full max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Trenutni set na vozilu - za demontažu</h1>
        <p className="text-center text-md text-slate-500 mb-8">Odaberite u koje skladište ide trenutni set nakon demontaže.</p>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm ">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Trenutni set za demontažu</div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Kod seta">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{activeSetOnVehicle.code}</span>
                    <Badge text="Montiran" variant="info" height="small" />
                  </div>
                </InfoRow>
                <InfoRow label="Dimenzija">{activeDimension || "-"}</InfoRow>
                <InfoRow label="Tip gume">{activeTireType?.name ?? "-"}</InfoRow>
                <InfoRow label="Proizvođač">{activeManufacturers || "-"}</InfoRow>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm ">
            <div className="px-5 py-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Gdje ide set?</div>
              <WarehouseDropdown
                name="oldSetWarehouseId"
                label="Skladište za demontirani set"
                placeholder="Odaberite skladište gdje se spremaju gume"
                value={formValues.oldSetWarehouseId}
                onChange={handleDropdownChange}
                error={errors.oldSetWarehouseId}
                required
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(MountStep2);
